import { Button } from '../ui/Button';
import { field } from '../create/Field';
import { useDraftChoice } from './FactSelect';
import type { Member } from './types';

/**
 * Who owns the ticket. The choice is staged and "Assign" sends it (Codex review of ticket 04):
 * a select that assigned on `change` assigned, and notified, every person the arrow keys
 * passed through on Windows. "Take it" assigns it to the caller in one press.
 */
export function OwnerPicker({
  id,
  owner,
  owners,
  me,
  canTake,
  busy,
  onAssign,
}: {
  id: string;
  owner: { id: string; name: string | null } | null;
  /** Everyone who may own a ticket (owner and office). */
  owners: readonly Member[];
  me: string;
  canTake: boolean;
  busy: boolean;
  onAssign: (ownerId: string) => void;
}) {
  const { shown, draft, setDraft } = useDraftChoice(owner?.id ?? '');
  return (
    <span className="flex flex-wrap items-center justify-end gap-2">
      <select
        id={id}
        className={`${field} sm:w-56`}
        value={shown}
        onChange={(e) => setDraft(e.target.value)}
      >
        {owner === null ? (
          <option value="" disabled>
            Nobody yet
          </option>
        ) : null}
        {owner && !owners.some((m) => m.userId === owner.id) ? (
          <option value={owner.id} disabled>
            {owner.name ?? 'Former staff'}
          </option>
        ) : null}
        {owners.map((m) => (
          <option key={m.userId} value={m.userId}>
            {m.name ?? m.email}
          </option>
        ))}
      </select>
      {draft ? (
        <Button
          size="sm"
          aria-disabled={busy || undefined}
          onClick={() => {
            if (!busy) onAssign(draft);
          }}
        >
          Assign
        </Button>
      ) : null}
      {owner?.id !== me && canTake ? (
        <Button
          variant="ghost"
          size="sm"
          aria-disabled={busy || undefined}
          onClick={() => {
            if (!busy) onAssign(me);
          }}
        >
          Take it
        </Button>
      ) : null}
    </span>
  );
}
